import React from "react";
import "./App.css";
import "./theme-mv.css";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Layout from "./components/Layout";
import Placeholder from "./pages/Placeholder";
import NotFound from "./pages/NotFound";
import { trackPageView } from "./lib/analytics";

function RouteTracker() {
  const location = useLocation();
  React.useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}
import FlowChart from "./pages/FlowChart";
import Guru from "./pages/Guru";
import DispatchBoard from "./pages/DispatchBoard";
import MobileDispatchBoard from "./pages/mobile/MobileDispatchBoard";
import Customers from "./pages/Customers";
import MobileCustomers from "./pages/mobile/MobileCustomers";
import JobHistory from "./pages/JobHistory";
import MobileEstimates from "./pages/mobile/MobileEstimates";
import Technicians from "./pages/Technicians";
import Jobs from "./pages/Jobs";
import MobileJobs from "./pages/mobile/MobileJobs";
import Estimates from "./pages/Estimates";
import Pipeline from "./pages/Pipeline";
import MobilePipeline from "./pages/mobile/MobilePipeline";
import Pricebook from "./pages/Pricebook";
import MobileHome from "./pages/mobile/MobileHome";
import MobileJobDetail from "./pages/MobileJobDetail";
import MobileGuru from "./pages/MobileGuru";
import MobileCalendar from "./pages/MobileCalendar";
import MobileJobHistory from "./pages/mobile/MobileJobHistory";
import MobileAllActivity from "./pages/mobile/MobileAllActivity";
import MobileVoiceCommands from "./pages/mobile/MobileVoiceCommands";
import Reports from "./pages/Reports";
import Marketing from "./pages/Marketing";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import TenantSmsPrivacy from "./pages/TenantSmsPrivacy";
import TenantSmsTerms from "./pages/TenantSmsTerms";
import Automations from "./pages/Automations";
import AiAgents from "./pages/AiAgents";
import Tasks from "./pages/Tasks";
import Calendar from "./pages/Calendar";
import PublicForm from "./pages/PublicForm";
import CompanySettings from "./pages/CompanySettings";
import Billing from "./pages/Billing";
import PlatformLogin from "./pages/PlatformLogin";
import PlatformAdmin from "./pages/PlatformAdmin";
import AffiliateLogin from "./pages/affiliate/AffiliateLogin";
import AffiliateDashboard from "./pages/affiliate/AffiliateDashboard";
import AffiliatePayroll from "./pages/affiliate/AffiliatePayroll";
import AffiliateProfile from "./pages/affiliate/AffiliateProfile";
import AffiliatePayouts from "./pages/affiliate/AffiliatePayouts";
import BecomeAffiliate from "./pages/BecomeAffiliate";
import SignupCompany from "./pages/SignupCompany";
import Pricing from "./pages/Pricing";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AcceptInvite from "./pages/AcceptInvite";
import AuthCallback from "./pages/AuthCallback";
import MarketingPreview from "./pages/MarketingPreview";
import FeaturesIndex from "./pages/marketing/FeaturesIndex";
import FeaturePage from "./pages/marketing/FeaturePage";
import IndustriesIndex from "./pages/marketing/IndustriesIndex";
import IndustryPage from "./pages/marketing/IndustryPage";
import SolutionsIndex from "./pages/marketing/SolutionsIndex";
import SolutionPage from "./pages/marketing/SolutionPage";
import ResourcesHub from "./pages/marketing/ResourcesHub";
import BlogIndex from "./pages/marketing/BlogIndex";
import ArticleDetail from "./pages/marketing/ArticleDetail";
import { GuidesIndex, ToolsIndex } from "./pages/marketing/ResourcesComingSoon";
import PayInvoice from "./pages/PayInvoice";
import PayDeposit from "./pages/PayDeposit";
import AcceptEstimate from "./pages/AcceptEstimate";
import PublicReview from "./pages/PublicReview";
import PublicContact from "./pages/PublicContact";
import ProtectedRoute from "./components/ProtectedRoute";
import RoleRoute from "./components/RoleRoute";
import RoleHome from "./components/RoleHome";
import RootRoute from "./components/RootRoute";
import { AuthProvider } from "./contexts/AuthContext";
import { ChatProvider } from "./contexts/ChatContext";
import { PlatformBrandingProvider } from "./contexts/PlatformBrandingContext";
import { TaskReminderProvider } from "./contexts/TaskReminderProvider";
import { LocationShareProvider } from "./contexts/LocationShareContext";
import { NavigationHistoryProvider } from "./contexts/NavigationHistoryContext";
import { ModuleGate } from "./components/ModuleGate";
import Messages from "./pages/Messages";
import MobileMessages from "./pages/mobile/MobileMessages";
import Inbox from "./pages/Inbox";
import MobileInbox from "./pages/mobile/MobileInbox";
import { Toaster } from "./components/ui/toaster";
import { Toaster as SonnerToaster } from "./components/ui/sonner";
import PWAUpdatePrompt from "./components/PWAUpdatePrompt";
import PushPermissionPrompt from "./components/PushPermissionPrompt";
import MobileNewMessageBanner from "./components/MobileNewMessageBanner";
import StaffOnboarding from "./components/StaffOnboarding";
import CompanyOnboarding from "./components/CompanyOnboarding";
import ClockInPromptHost from "./components/ClockInPromptHost";
import CompanyPopupHost from "./components/CompanyPopupHost";
import MobileNavHost from "./components/mobile/MobileNavHost";
import VoiceAgent from "./components/voice/VoiceAgent";
import MobileTopBarHost from "./components/mobile/MobileTopBarHost";
import LowCreditsAutoPopup from "./components/LowCreditsAutoPopup";
import { DesktopOnly, ResponsivePage, MobileOnly } from "./components/mobile/DesktopOnlyScreen";

function App() {
  return (
    <HelmetProvider>
    <div className="App">
      <BrowserRouter>
        <PlatformBrandingProvider>
        <AuthProvider>
          <ChatProvider>
          <TaskReminderProvider>
          <LocationShareProvider>
          <NavigationHistoryProvider>
          <RouteTracker />
          <Routes>
            {/* Public marketing landing page (servicegurupro.com home) */}
            <Route path="/" element={<RootRoute />} />

            {/* Public auth routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Navigate to="/signup" replace />} />
            <Route path="/signup" element={<SignupCompany />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/legal/:slug/privacy" element={<TenantSmsPrivacy />} />
            <Route path="/legal/:slug/sms-terms" element={<TenantSmsTerms />} />
            {/* SEO site architecture — Features / Industries / Solutions */}
            <Route path="/features" element={<FeaturesIndex />} />
            <Route path="/features/:slug" element={<FeaturePage />} />
            <Route path="/industries" element={<IndustriesIndex />} />
            <Route path="/industries/:slug" element={<IndustryPage />} />
            <Route path="/solutions" element={<SolutionsIndex />} />
            <Route path="/solutions/:slug" element={<SolutionPage />} />
            <Route path="/resources" element={<ResourcesHub />} />
            <Route path="/resources/blog" element={<BlogIndex />} />
            <Route path="/resources/blog/:slug" element={<ArticleDetail />} />
            <Route path="/resources/guides" element={<GuidesIndex />} />
            <Route path="/resources/guides/:slug" element={<ArticleDetail />} />
            <Route path="/resources/tools" element={<ToolsIndex />} />
            <Route path="/resources/tools/:slug" element={<ArticleDetail />} />
            {/* Standalone marketing design preview — review only, no backend/auth wiring */}
            <Route path="/design-preview" element={<MarketingPreview />} />

            {/* Hidden platform control plane (super-admin only) */}
            <Route path="/platform/login" element={<PlatformLogin />} />
            <Route path="/platform" element={<PlatformAdmin />} />
            {/* Affiliate/partner portal — its own login session, unrelated to tenant auth */}
            <Route path="/affiliate/login" element={<AffiliateLogin />} />
            <Route path="/affiliate/dashboard" element={<AffiliateDashboard />} />
            <Route path="/affiliate/payroll" element={<AffiliatePayroll />} />
            <Route path="/affiliate/profile" element={<AffiliateProfile />} />
            <Route path="/affiliate/payouts" element={<AffiliatePayouts />} />
            <Route path="/become-an-affiliate" element={<BecomeAffiliate />} />
            {/* Public invoice payment page (no auth — customer pays from emailed link) */}
            <Route path="/pay/:invoiceId" element={<PayInvoice />} />
            {/* Public estimate deposit page (no auth) */}
            <Route path="/pay/deposit/:estimateId" element={<PayDeposit />} />
            {/* Public estimate acceptance page — review, sign, pay deposit (no auth) */}
            <Route path="/accept/:estimateId" element={<AcceptEstimate />} />
            <Route path="/review/:token" element={<PublicReview />} />
            <Route path="/contact" element={<PublicContact />} />
            {/* Public embeddable lead-capture form (no auth) */}
            <Route path="/f/:formId" element={<PublicForm />} />

            {/* Mobile home — shared surface for every role (identical shell + tabs) */}
            <Route
              path="/mobile"
              element={
                <ProtectedRoute>
                  <RoleRoute roles={["admin", "dispatcher", "technician"]}>
                    <ModuleGate module="mobile_tech"><MobileOnly><MobileHome /></MobileOnly></ModuleGate>
                  </RoleRoute>
                </ProtectedRoute>
              }
            />
            {/* Mobile job detail — full-screen tech job view */}
            <Route
              path="/mobile/jobs/:id"
              element={
                <ProtectedRoute>
                  <RoleRoute roles={["admin", "dispatcher", "technician"]}>
                    <MobileJobDetail />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />
            {/* Mobile Guru — full-screen AI chat */}
            <Route
              path="/mobile/guru"
              element={
                <ProtectedRoute>
                  <RoleRoute roles={["admin", "dispatcher", "technician", "office_personnel"]}>
                    <ModuleGate module="guru"><MobileGuru /></ModuleGate>
                  </RoleRoute>
                </ProtectedRoute>
              }
            />
            {/* Mobile Calendar */}
            <Route
              path="/mobile/calendar"
              element={
                <ProtectedRoute>
                  <RoleRoute roles={["admin", "dispatcher", "technician"]}>
                    <ModuleGate module="calendar"><MobileCalendar /></ModuleGate>
                  </RoleRoute>
                </ProtectedRoute>
              }
            />
            {/* Mobile All Activity — full activity feed with date filter, linked from the dashboard's Recent Activity card */}
            <Route
              path="/mobile/activity"
              element={
                <ProtectedRoute>
                  <ModuleGate module="all_activity"><MobileAllActivity /></ModuleGate>
                </ProtectedRoute>
              }
            />
            {/* Voice Commands help page — reachable from the mobile drawer, any role */}
            <Route
              path="/voice-commands"
              element={
                <ProtectedRoute>
                  <MobileVoiceCommands />
                </ProtectedRoute>
              }
            />

            {/* Protected app routes */}
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<RoleHome />} />
              <Route path="/messages" element={<ModuleGate module="messaging"><ResponsivePage mobile={<MobileMessages />} desktop={<Messages />} /></ModuleGate>} />
              <Route path="/inbox" element={<RoleRoute roles={["admin", "dispatcher"]}><ModuleGate module="inbox"><ResponsivePage mobile={<MobileInbox />} desktop={<Inbox />} /></ModuleGate></RoleRoute>} />
              <Route path="/dispatch" element={<RoleRoute roles={["admin", "dispatcher"]} perm="dispatch.view"><ModuleGate module="dispatch"><ResponsivePage mobile={<MobileDispatchBoard />} desktop={<DispatchBoard />} /></ModuleGate></RoleRoute>} />
              <Route path="/jobs" element={<RoleRoute roles={["admin", "dispatcher", "technician"]} perm="jobs.view"><ResponsivePage mobile={<MobileJobs />} desktop={<Jobs />} /></RoleRoute>} />
              <Route path="/customers" element={<RoleRoute roles={["admin", "dispatcher", "technician"]} perm="customers.view"><ResponsivePage mobile={<MobileCustomers />} desktop={<Customers />} /></RoleRoute>} />
              <Route path="/history" element={<RoleRoute roles={["admin", "dispatcher", "office_personnel", "technician"]}><ResponsivePage mobile={<MobileJobHistory />} desktop={<JobHistory />} /></RoleRoute>} />
              <Route path="/technicians" element={<RoleRoute roles={["admin", "dispatcher"]} perm="staff.view"><Technicians /></RoleRoute>} />
              <Route path="/estimates" element={<RoleRoute roles={["admin", "dispatcher", "technician"]} perm="estimates.view"><ResponsivePage mobile={<MobileEstimates />} desktop={<Estimates />} /></RoleRoute>} />
              <Route path="/pipeline" element={<RoleRoute roles={["admin", "dispatcher", "technician", "office_personnel"]}><ModuleGate module="pipeline"><ResponsivePage mobile={<MobilePipeline />} desktop={<Pipeline />} /></ModuleGate></RoleRoute>} />
              <Route path="/pricebook" element={<RoleRoute roles={["admin", "dispatcher", "technician"]} perm="pricebook.view"><ModuleGate module="pricebook"><Pricebook /></ModuleGate></RoleRoute>} />
              <Route path="/reports" element={<RoleRoute roles={["admin", "dispatcher"]} perm="reports.view"><ModuleGate module="reports"><DesktopOnly pathname="/reports"><Reports /></DesktopOnly></ModuleGate></RoleRoute>} />
              <Route path="/marketing" element={<RoleRoute roles={["admin", "dispatcher"]} perm="marketing.view"><ModuleGate module="marketing"><DesktopOnly pathname="/marketing"><Marketing /></DesktopOnly></ModuleGate></RoleRoute>} />
              <Route path="/automations" element={<RoleRoute roles={["admin", "dispatcher"]} perm="automations.view"><ModuleGate module="automations"><DesktopOnly pathname="/automations"><Automations /></DesktopOnly></ModuleGate></RoleRoute>} />
              <Route path="/tasks" element={<RoleRoute roles={["admin", "dispatcher", "technician"]} perm="tasks.view"><Tasks /></RoleRoute>} />
              <Route path="/calendar" element={<RoleRoute roles={["admin", "dispatcher", "technician", "office_personnel"]}><ModuleGate module="calendar"><Calendar /></ModuleGate></RoleRoute>} />
              <Route path="/flowchart" element={<ModuleGate module="flow_ai"><DesktopOnly pathname="/flowchart"><FlowChart /></DesktopOnly></ModuleGate>} />
              <Route path="/guru" element={<ModuleGate module="guru"><DesktopOnly pathname="/guru"><Guru /></DesktopOnly></ModuleGate>} />
              <Route path="/ai-agents" element={<RoleRoute roles={["admin", "dispatcher"]}><ModuleGate module="ai_agents"><DesktopOnly pathname="/ai-agents"><AiAgents /></DesktopOnly></ModuleGate></RoleRoute>} />
              {/* User Management merged into Staff page — keep the old link working */}
              <Route path="/users" element={<Navigate to="/technicians" replace />} />
              <Route
                path="/settings/company"
                element={
                  <RoleRoute roles={["admin"]} perm="settings.view">
                    <CompanySettings />
                  </RoleRoute>
                }
              />
              <Route
                path="/settings/billing"
                element={
                  <RoleRoute roles={["admin"]}>
                    <Billing />
                  </RoleRoute>
                }
              />
            </Route>

            {/* Catch-all — soft 404 for any unmatched public route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <StaffOnboarding />
          <CompanyOnboarding />
          <ClockInPromptHost />
          <CompanyPopupHost />
          <MobileNavHost />
          <VoiceAgent hideLauncher />
          <MobileTopBarHost />
          <LowCreditsAutoPopup />
          <PushPermissionPrompt />
          <MobileNewMessageBanner />
          </NavigationHistoryProvider>
          </LocationShareProvider>
          </TaskReminderProvider>
          </ChatProvider>
        </AuthProvider>
        </PlatformBrandingProvider>
      </BrowserRouter>
      <Toaster />
      <SonnerToaster position="bottom-right" richColors />
      <PWAUpdatePrompt />
    </div>
    </HelmetProvider>
  );
}

export default App;
